import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiMinus, FiCheckCircle } = FiIcons;

const Results = ({ results }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (percentage) => {
    return `${percentage.toFixed(1)}%`;
  };

  const getMarginMessage = (margin) => {
    if (margin >= 60) {
      return {
        message: "This result suggests the package is highly profitable. It leaves strong room for reinvestment, sustainability, and business growth.",
        icon: FiCheckCircle,
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200"
      };
    } else if (margin >= 50) {
      return {
        message: "This package appears well-priced and profitable. It should support both delivery and long-term viability.",
        icon: FiTrendingUp,
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200"
      };
    } else if (margin >= 40) {
      return {
        message: "This may be viable in the short term, but your margin is lean. Consider reviewing pricing or cost assumptions.",
        icon: FiMinus,
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200"
      };
    } else {
      return {
        message: "This margin is likely too low to sustain a healthy business. Consider adjusting price, scope, or delivery methods.",
        icon: FiTrendingDown,
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200"
      };
    }
  };

  const marginInfo = getMarginMessage(results.margin);

  // New section for referral fee
  const hasReferralFee = results.referralFee > 0;

  const resultItems = [
    { 
      label: 'Package Price (inc. GST)',
      value: formatCurrency(results.adjustedPackagePrice + results.referralFee),
      type: 'standard'
    },
  ];
  
  // Only add referral fee if there is one
  if (hasReferralFee) {
    resultItems.push({
      label: 'Referral Fee',
      value: `- ${formatCurrency(results.referralFee)}`,
      type: 'deduction'
    });
    
    resultItems.push({
      label: 'Adjusted Package Price',
      value: formatCurrency(results.adjustedPackagePrice),
      type: 'subtotal'
    });
  }
  
  resultItems.push(
    { label: 'GST Component', value: `- ${formatCurrency(results.gst)}`, type: 'deduction' },
    { label: 'Net Revenue', value: formatCurrency(results.netRevenue), type: 'subtotal' },
    { label: 'Total Delivery Costs', value: `- ${formatCurrency(results.totalDeliveryCosts)}`, type: 'deduction' },
    { label: 'Gross Profit', value: formatCurrency(results.grossProfit), type: 'subtotal' },
    { label: 'Estimated Tax', value: `- ${formatCurrency(results.estimatedTax)}`, type: 'deduction' },
    { label: 'After-Tax Profit', value: formatCurrency(results.afterTaxProfit), type: 'result' },
    { label: 'Final Margin', value: formatPercentage(results.margin), type: 'highlight' }
  );

  return (
    <motion.div
      className="bg-white rounded-xl shadow-lg p-6"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Profitability Analysis
      </h2>

      {/* Results List */}
      <div className="mb-6 overflow-hidden rounded-lg border border-gray-200">
        {resultItems.map((item, index) => (
          <motion.div
            key={item.label}
            className={`flex justify-between p-4 ${
              index !== resultItems.length - 1 ? 'border-b border-gray-200' : ''
            } ${
              item.type === 'subtotal' ? 'bg-gray-50 font-medium' :
              item.type === 'result' ? 'bg-blue-50 font-semibold' :
              item.type === 'highlight' ? 'bg-blue-100 font-bold' :
              item.type === 'deduction' ? 'text-gray-600' : ''
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <div className="text-sm">{item.label}</div>
            <div className={`text-sm ${
              item.type === 'highlight' ? 'text-blue-700' : 
              item.type === 'result' ? 'text-blue-600' : ''
            }`}>
              {item.value}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Margin Assessment */}
      <motion.div
        className={`p-4 rounded-lg border ${marginInfo.bgColor} ${marginInfo.borderColor}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <div className="flex items-start space-x-3">
          <SafeIcon
            icon={marginInfo.icon}
            className={`${marginInfo.color} text-xl mt-0.5 flex-shrink-0`}
          />
          <p className={`${marginInfo.color} text-sm leading-relaxed`}>
            {marginInfo.message}
          </p>
        </div>
      </motion.div>

      {/* Disclaimer */}
      <motion.div
        className="mt-6 pt-4 border-t border-gray-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <p className="text-xs text-gray-500 leading-relaxed">
          <strong>Disclaimer:</strong> This tool provides general information only and does not constitute financial or legal advice. Results are estimates only. Please consult a registered accountant or commercial lawyer for personalised advice.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default Results;